import { CabButton } from "@CabComponents/CabButton";
import { CabIcon } from "@CabComponents/CabIcon";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { Alert, Box, Divider, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Controller, useFieldArray, useForm, useFormContext, FormProvider } from "react-hook-form";
import { emailRegex } from "../../constants";
import { Attendee } from "./PollVotingParticipant";
import { IoCloseCircleOutline, IoAdd, IoAddOutline } from "react-icons/io5";
import { useEffect, useCallback, useState } from "react";
import { CabModal } from "@CabComponents/CabModal";
import colors from "../../colors";
import { CabTooltip } from "@CabComponents/CabTooltip";
import { CabToggleChip } from "@CabComponents/CabToggleChip";

export type AttendeeInfoFormProps = {
  from: string;
  participants?: PollAttendeeOption[];
  defaultSelectedAttendees: Attendee[];
  onFormStateChange?: (isValid: boolean, submit: () => void) => void;
  enablePublicAttendeeList?: boolean;
  allowAddAttendees?: boolean;
  onSelectParticipants: (participants: SelectedPollAttendee[]) => void,
};

type AttendeeFormData = {
  attendees: Attendee[];
};

type PollAttendeeOption = {
  name: string;
  email: string;
  first_response_date: string | null;
  required?: boolean;
  voterType?: 'participant' | 'leader';
  meetingLeaderId?: number;
  leaderId?: number;
};

type SelectedPollAttendee = {
  name: string;
  email: string;
  required?: boolean;
  voterType?: 'participant' | 'leader';
  meetingLeaderId?: number;
  leaderId?: number;
};

const AttendeeInfoForm = ({ 
  from, participants = [], defaultSelectedAttendees, onFormStateChange,
  enablePublicAttendeeList, allowAddAttendees, onSelectParticipants
}: AttendeeInfoFormProps) => {
  const handleClickParticipant = (participant: PollAttendeeOption) => {
    let tempParticipants: SelectedPollAttendee[] = [...defaultSelectedAttendees];
    const index = tempParticipants.findIndex(attendee => attendee.email === participant.email);
    if (index === -1) {
      tempParticipants = [
        ...tempParticipants,
        {
          name: participant.name || '',
          email: participant.email || '',
          required: participant.required,
          voterType: participant.voterType,
          meetingLeaderId: participant.meetingLeaderId,
          leaderId: participant.leaderId,
        },
      ];
    } else {
      tempParticipants.splice(index, 1);
    }
    onSelectParticipants(tempParticipants);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}>
      <Typography
        variant="h1"
        sx={{
          fontSize: 36,
          fontWeight: 300,
          fontFamily: "sentient"
        }}>Hello!</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
        <Typography
          variant="body2"
          color={colors.calmGrey600}
          sx={{
            fontSize: 16,
            maxWidth: '480px'
          }}>
          <strong>{from}</strong> has invited you to choose preferred times for this meeting.
        </Typography>

        {enablePublicAttendeeList ? (
          <Typography
            variant="body2"
            color={colors.calmGrey600}
            sx={{
              fontSize: 16,
              maxWidth: 600
            }}>
            Please select yourself, or the people you support with scheduling:
          </Typography>
        ) : (
          <Typography variant="body2" color={colors.calmGrey600} sx={{
            fontSize: 16
          }}>
            To get started, enter the details for the attendee of this meeting.
          </Typography>
        )}
      </Box>
      {enablePublicAttendeeList ? (
        <Box
          sx={{
            width: '100%',
            maxWidth: '780px'
          }}>
          <MeetingParticipantSelector 
            onClick={handleClickParticipant} 
            selectedParticipants={defaultSelectedAttendees}
            participants={participants}
            allowAddAttendees={allowAddAttendees}
            onSelectParticipants={onSelectParticipants}
            onFormStateChange={onFormStateChange}
          />
        </Box>
      ) : (
        <AttendeeFormSection 
          defaultSelectedAttendees={defaultSelectedAttendees}
          onSelectParticipants={onSelectParticipants}
          onFormStateChange={onFormStateChange}
        />
      )}
    </Box>
  );
};

export default AttendeeInfoForm;


type AttendeeFormSectionProps = {
  defaultSelectedAttendees: Attendee[];
  onSelectParticipants: (participants: SelectedPollAttendee[]) => void;
  onFormStateChange?: (isValid: boolean, submit: () => void) => void;
};

const AttendeeFormSection = ({
  defaultSelectedAttendees, onSelectParticipants, onFormStateChange
}: AttendeeFormSectionProps) => {
  const defaultAttendee: Attendee = { name: '', email: '', emailHash: '', noTimesComment: null };
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const methods = useForm<AttendeeFormData>({
    defaultValues: { 'attendees': defaultSelectedAttendees.length ? defaultSelectedAttendees : [defaultAttendee] },
    delayError: 1000,
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({control: methods.control, name: 'attendees'});

  const onSubmit = useCallback(() => methods.handleSubmit((values) => {
    onSelectParticipants(values.attendees);
  })(), [methods, onSelectParticipants]);

  useEffect(() => {
    onFormStateChange?.(methods.formState.isValid, onSubmit);
  }, [methods.formState.isValid, onSubmit, onFormStateChange]);

  return (
    <Box
      sx={{
        maxWidth: '300px',
        gap: 2,
        display: 'flex',
        flexDirection: 'column'
      }}>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}>
            {fields.map((attendee, index) => {
              return (
                <Box
                  key={attendee.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                  {index !== 0 && <Divider sx={{ marginBottom: 1 }} />}
                  <Box
                    sx={{
                      maxWidth: '500px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mb: 0.5
                      }}>
                      {index !== 0 && 
                      <IconButton edge='end' aria-label='remove' disableRipple sx={{padding: 0}}
                        onClick={() => remove(index)}
                      >
                        <CabIcon Icon={IoCloseCircleOutline} alt='Remove' />
                      </IconButton>
                      }
                    </Box>
                    <AttendeeForm 
                      index={index}
                      showTitle={false}
                      existingParticipants={fields.filter((_, i) => i !== index).map(f => ({ 
                        name: f.name, 
                        email: f.email, 
                        first_response_date: null 
                      }))}
                    />
                  </Box>
                </Box>
              );
            })}
            {!isSmallScreen && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  width: '100%'
                }}>
                <CabButton
                  buttonType="tertiary"
                  onClick={() => append(defaultAttendee)}
                  icon={<CabIcon Icon={IoAdd} alt='Add'/>}
                  sx={{ flex: 3 }}
                >
                  Add Attendee
                </CabButton>
                <CabButton
                  onClick={onSubmit}
                  disabled={!methods.formState.isValid}
                  sx={{ flex: 2 }}
                >
                  Continue
                </CabButton>
              </Box>
            )}
            {isSmallScreen && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  width: '100%'
                }}>
                <CabButton
                  onClick={onSubmit}
                  disabled={!methods.formState.isValid}
                  sx={{ width: '100%' }}
                >
                  Continue
                </CabButton>
              </Box>
            )}
            <button type="submit" style={{ display: 'none' }} />
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

/* type SingleAttendeeFormData = {
  name: string;
  email: string;
}; */

type AttendeeFormProps = {
  index: number;
  onKeyDown?: (key: string) => void;
  existingParticipants?: { name: string; email: string; first_response_date: string | null }[];
  title?: string;
  showTitle?: boolean;
  onFormStateChange?: (isValid: boolean, submit: () => void) => void;
};

const AttendeeForm = ({ 
  index,
  onKeyDown, 
  existingParticipants = [], 
  title = "Enter attendee details",
  showTitle = true,
  onFormStateChange
}: AttendeeFormProps) => {
  const {
    control,
    formState
  } = useFormContext<AttendeeFormData>();

  useEffect(() => {
    onFormStateChange?.(formState.isValid, () => {});
  }, [formState.isValid, onFormStateChange]);

  return (
    <Box
      sx={{
        maxWidth: '300px',
        gap: 2,
        display: 'flex',
        flexDirection: 'column'
      }}>
      {showTitle && (
        <Typography sx={{maxWidth: 533, fontSize: 18}}>
          {title}
        </Typography>
      )}
      <Controller 
        name={`attendees.${index}.name`}
        rules={{
          required: {
            value: true,
            message: "Name is required"
          },
        }}
        control={control}
        render={({field: { ref: inputRef, ...field }, fieldState: { error }}) => {
          return <><CabTextInput
            {...field}
            inputRef={inputRef}
            onKeyDown={(e) => onKeyDown?.(e.key)}
            label="Name*"
          />
          {error && (
            <Alert severity="error" sx={{ marginTop: 1 }}>
              {error.message || "Invalid Name"}
            </Alert>
          )}
          </>;
        }}
      />
      <Controller 
        name={`attendees.${index}.email`}
        rules={{
          required: {
            value: true,
            message: "Email is required"
          },
          pattern: {
            value: emailRegex,
            message: "Invalid Email"
          },
          validate: (email) => {
            if (existingParticipants.length > 0) {
              const existingParticipant = existingParticipants.find(p => 
                p.email.toLowerCase() === email.toLowerCase()
              );
              if (existingParticipant) {
                return `There is already an attendee with that email address (${existingParticipant.name})`;
              }
            }
            return true;
          },
        }}
        control={control}
        render={({field: { ref: inputRef, ...field }, fieldState: { error }}) => {
          return <><CabTextInput
            {...field}
            inputRef={inputRef}
            onKeyDown={(e) => onKeyDown?.(e.key)}
            label="Email"
            required
          />
          {error && (
            <Alert severity="error" sx={{ marginTop: 1 }}>
              {error.message || "Invalid Email"}
            </Alert>
          )}
          </>;
        }}
      />
    </Box>
  );
};

AttendeeForm.displayName = 'AttendeeForm';

type MeetingParticipantSelectorProps = {
  participants: PollAttendeeOption[];
  selectedParticipants: SelectedPollAttendee[];
  onClick: (participant: PollAttendeeOption) => void;
  allowAddAttendees?: boolean;
  onSelectParticipants: (participants: SelectedPollAttendee[]) => void;
  onFormStateChange?: (isValid: boolean, submit: () => void) => void;
};

const MeetingParticipantSelector = ({
  participants, 
  onClick, 
  selectedParticipants,
  allowAddAttendees,
  onSelectParticipants,
  onFormStateChange
}: MeetingParticipantSelectorProps) => {
  const [attendeeModalOpen, setAttendeeModalOpen] = useState(false);
  const [addedParticipants, setAddedParticipants] = useState<PollAttendeeOption[]>([]);
  const [modalFormValid, setModalFormValid] = useState(false);
  const [localSelectedParticipants, setLocalSelectedParticipants] = useState(selectedParticipants);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    onFormStateChange?.(localSelectedParticipants.length > 0, () => {
      onSelectParticipants(localSelectedParticipants);
    });
  }, [localSelectedParticipants, onFormStateChange, onSelectParticipants]);

  const handleClickParticipant = (participant: PollAttendeeOption) => {
    let tempParticipants: SelectedPollAttendee[] = [...localSelectedParticipants];
    const index = tempParticipants.findIndex(attendee => attendee.email === participant.email);
    const selectedParticipant = {
      name: participant.name || '',
      email: participant.email || '',
      required: participant.required,
      voterType: participant.voterType,
      meetingLeaderId: participant.meetingLeaderId,
      leaderId: participant.leaderId,
    };
    if (index === -1) {
      // On small screens, only allow one participant
      if (isSmallScreen) {
        tempParticipants = [selectedParticipant];
      } else {
        tempParticipants = [
          ...tempParticipants,
          selectedParticipant,
        ];
      }
    } else {
      tempParticipants.splice(index, 1);
    }
    setLocalSelectedParticipants(tempParticipants);
  };

  const methods = useForm<AttendeeFormData>({
    defaultValues: { 
      attendees: [{ name: '', email: '', emailHash: '', noTimesComment: null }] 
    },
    mode: 'onChange',
    delayError: 1000
  });

  const submitNewAttendee = () => {
    const values = methods.getValues();
    const attendee = values.attendees[0];
    
    setAttendeeModalOpen(false);
    
    const newParticipant = { 
      name: attendee.name, 
      email: attendee.email, 
      first_response_date: null,
      required: true,
    };
    
    setAddedParticipants([
      ...addedParticipants, 
      newParticipant
    ]);
    
    setLocalSelectedParticipants([
      ...localSelectedParticipants,
      { name: attendee.name, email: attendee.email, required: true }
    ]);
  };

  const cancelModal = () => {
    setAttendeeModalOpen(false);
  };

  const sortedParticipants = [...participants, ...addedParticipants].sort((a, b) => {
    const labelA = a?.name || a?.email || '';
    const labelB = b?.name || b?.email || '';
    return labelA.localeCompare(labelB);
  });

  return (
    <>
      <Box
        sx={{
          gap: 1,
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: sortedParticipants.length <= 8 ? 'column' : 'row'
        }}>
        {sortedParticipants.map(participant => {
          const displayName = participant.name ? participant.name : participant.email || '';
          const selected = localSelectedParticipants.find(attendee => attendee.email === participant.email);

          return (
            <Box
              key={participant.email}
              onClick={() => handleClickParticipant(participant)}
              sx={{
                maxWidth: 350,
                minWidth: 250,
                cursor: "pointer"
              }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  padding: 2,
                  height: 43,
                  borderRadius: 1,
                  backgroundColor: colors.white800,
                  border: selected ? 2 : 0,
                  "&:hover": { backgroundColor: colors.white700 }
                }}>
                <CabTooltip title={displayName} placement="right">
                  <Typography sx={{
                    fontWeight: participant.first_response_date ? "initial" : 600, color: colors.black800,
                    maxWidth: 150, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden'
                  }}>
                    {displayName}
                  </Typography>
                </CabTooltip>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75
                  }}>
                  {participant.first_response_date &&
                    <CabTooltip title="Select this person to edit availability" placement="top" wrapWithSpan>
                      <CabToggleChip 
                        label='Responded' sx={{fontSize: 10, height: 18, paddingLeft: '4px', paddingRight: '4px'}}
                      />
                    </CabTooltip>
                  }
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      {allowAddAttendees ? (
        <>
          <Typography
            variant="body2"
            color={colors.black700}
            sx={{
              marginLeft: 1,
              marginTop: 2
            }}>
            Missing someone?
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: '100%',
              maxWidth: 300
            }}>
            <CabButton 
              buttonType="text"
              onClick={() => setAttendeeModalOpen(true)}
              icon={<CabIcon Icon={IoAddOutline} />}
            >
              Add Attendee
            </CabButton>
            <CabButton
              onClick={() => onSelectParticipants(localSelectedParticipants)}
              disabled={!localSelectedParticipants.length}
              sx={{ flex: 2 }}
            >
              Continue
            </CabButton>
          </Box>
        </>
      ) : (
        <CabButton
          onClick={() => onSelectParticipants(localSelectedParticipants)}
          disabled={!localSelectedParticipants.length}
          sx={{ flex: 2, marginTop: 2 }}
        >
          Continue
        </CabButton>
      )}
      <FormProvider {...methods}>
        <CabModal 
          open={attendeeModalOpen} 
          title="New Attendee"
          closeIcon={true}
          onClose={() => cancelModal()}
          actionButtons={<>
            <CabButton onClick={() => cancelModal()} buttonType="secondary">Cancel</CabButton>
            <CabButton 
              disabled={!modalFormValid} 
              onClick={methods.handleSubmit(submitNewAttendee)}
            >
              Submit
            </CabButton>
          </>}
        >
          <AttendeeForm 
            index={0}
            existingParticipants={[...participants, ...addedParticipants]}
            title="Enter new attendee details"
            onFormStateChange={setModalFormValid}
          />
        </CabModal>
      </FormProvider>
    </>
  );
};
